import { ChangeDetectorRef, Injectable } from '@angular/core';
import {
  catchError,
  debounce,
  first,
  firstValueFrom, Observable,
  of, OperatorFunction,
  Subject,
  switchMap,
  takeUntil,
  tap,
  timer,
} from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { VALUE_CHANGES_INTERVAL } from '../../constants';

@Injectable()
export class ObservableChainService {
  constructor() {
  }

  /**
   * Creates a default observable chain options object.
   * @param ref Change detector ref from an OnPush component, used to update UI async.
   * @param destroy$ Destroy subject of a component, will be used to unsubscribe observables.
   */
  public createOptions(ref: ChangeDetectorRef, destroy$: Subject<void>): ObservableChainOptions {
    return {
      error: null,
      loading: false,
      debounce: true,
      ref,
      destroy$,
    };
  }

  /**
   * After each form change call an API returning an observable and act on returned data.
   * @param options Observable chain options.
   * @param valueChanges Form value change observable.
   * @param apiObservable API observable to be used.
   * @param responseHandler Returned data handler.
   */
  public pipeValueChangesToApi<T, R>(options: ObservableChainOptions,
                                     valueChanges: Observable<T>,
                                     apiObservable: (request: T) => Observable<R>,
                                     responseHandler: (data: R | null) => void): void {
    valueChanges.pipe(
      // debounce is conditional to allow immediate programmatic updates (set options.debounce = false)
      debounce(() => options.debounce ? timer(VALUE_CHANGES_INTERVAL) : timer(0)),
      tap(() => {
        options.debounce = true; // restore debounce
        options.loading = true; // set loading indicator
        options.ref.detectChanges(); // required for on-push components to update themselves async
      }),
      switchMap((value: T) => {
        return apiObservable(value).pipe(
          catchError((error: HttpErrorResponse | Error) => {
            if (!(error instanceof HttpErrorResponse)) {
              this.logRuntimeError(error);
            }

            options.error = error; // remember the error to allow showing it in UI
            return of(null); // returning null is an indication of error
          }),
        );
      }),
      tap((data) => {
        if (data) {
          // if we got data, it means there was no error
          options.error = null;
        }

        // error or not, indicate we are no longer loading
        options.loading = false;
      }),
      // do not fire the sub handler if component was unloaded after starting an API request
      takeUntil(options.destroy$),
    ).subscribe(data => {
      // component should decide on how to deal with null data, no assumption is being made here
      // possible options: 1. load an empty list / initial value, 2. keep the old value etc.
      responseHandler(data);

      // required for on-push components to update themselves async
      options.ref.detectChanges();
    });
  }

  /**
   * Perform an API request returning an observable, then act on the returned data.
   * @param options Observable chain options.
   * @param apiObservable API observable to be used.
   * @param responseHandler Returned data handler.
   */
  public pipeApiRequest<R>(options: ObservableChainOptions,
                           apiObservable: () => Observable<R>,
                           responseHandler: (data: R | null) => void): void {
    let observable: Observable<R> | null = null;
    try {
      observable = apiObservable();
    } catch (e) {
      this.logRuntimeError(e as Error);
      options.error = e as Error;
      responseHandler(null);
      options.ref.detectChanges(); // required for on-push components to update themselves async
      return;
    }

    options.loading = true; // set loading indicator
    options.ref.detectChanges(); // required for on-push components to update themselves async
    observable.pipe(
      first(), // will unsubscribe as soon as this request completes
      catchError((error: HttpErrorResponse | Error) => {
        if (!(error instanceof HttpErrorResponse)) {
          this.logRuntimeError(error);
        }

        options.error = error; // remember the error to allow showing it in UI
        return of(null); // returning null is an indication of error
      }),
      tap((data) => {
        if (data) {
          // if we got data, it means there was no error
          options.error = null;
        }

        // error or not, indicate we are no longer loading
        options.loading = false;
      }),
      // do not fire the sub handler if component was unloaded after starting an API request
      takeUntil(options.destroy$),
    ).subscribe(data => {
      // component should decide on how to deal with null data, no assumption is being made here
      // possible options: 1. load an empty list / initial value, 2. keep the old value etc.
      responseHandler(data);

      // required for on-push components to update themselves async
      options.ref.detectChanges();
    });
  }

  wrapIntoPromise<R>(options: ObservableChainOptions,
                     apiObservable: () => Observable<R>): Promise<R> {
    let observable: Observable<R> | null = null;
    try {
      observable = apiObservable();
    } catch (e) {
      options.error = e as Error;
      options.ref.detectChanges(); // required for on-push components to update themselves async
      this.logRuntimeError(e as Error);
      return Promise.reject(e);
    }

    options.loading = true; // set loading indicator
    options.error = null;
    options.ref.detectChanges(); // required for on-push components to update themselves async

    return firstValueFrom(observable.pipe(
      catchError((error: HttpErrorResponse | Error) => {
        if (!(error instanceof HttpErrorResponse)) {
          this.logRuntimeError(error);
        }

        options.error = error; // remember the error to allow showing it in UI
        throw error;
      }),
    )).finally(() => {
      options.loading = false;
      options.ref.detectChanges();
    });
  }

  logRuntimeError(error: Error) {
    // runtime error - log stack trace for debugging purposes
    // logging to the console for now, can be later changed to DataDog or other centralized logging
    console.error(error);
  }

  /**
   * Apply our standard error handling mechanism in a custom rxjs scenario.
   * @param observableChainOptions Observable chain options.
   * @param stopLoading Determines if loading should be stopped once observable returns a value.
   * @remark It is developer's responsibility to set loading state and call detectChanges.
   */
  useOptions<T>(observableChainOptions: ObservableChainOptions, stopLoading: boolean = false): OperatorFunction<T, T | null> {
    return (source: Observable<T>) => source.pipe(
      catchError((error: HttpErrorResponse | Error) => {
        if (!(error instanceof HttpErrorResponse)) {
          this.logRuntimeError(error);
        }

        observableChainOptions.error = error;
        return of(null);
      }),
      tap((data: T | null) => {
        if (data) {
          observableChainOptions.error = null;
        }

        if (stopLoading) {
          observableChainOptions.loading = false;
        }
      }),
      takeUntil(observableChainOptions.destroy$),
    );
  }

  startUseOptions<T>(observableChainOptions: ObservableChainOptions, ref: ChangeDetectorRef): OperatorFunction<T, T> {
    observableChainOptions.loading = true;
    ref.detectChanges();

    return (source: Observable<T>) => source;
  }

  endUseOptions<T>(observableChainOptions: ObservableChainOptions, ref: ChangeDetectorRef): OperatorFunction<T, T | null> {
    return (source: Observable<T>) => source.pipe(
      catchError((error: HttpErrorResponse | Error) => {
        if (!(error instanceof HttpErrorResponse)) {
          this.logRuntimeError(error);
        }

        observableChainOptions.error = error;
        ref.detectChanges();

        return of(null);
      }),
      tap((data: T | null) => {
        if (data) {
          observableChainOptions.error = null;
        }

        observableChainOptions.loading = false;
        ref.detectChanges();
      }),
      takeUntil(observableChainOptions.destroy$),
    );
  }
}

export interface ObservableChainOptions {
  /**
   * Error that should be displayed to user.
   */
  error: HttpErrorResponse | Error | null,

  /**
   * UI indicator that observable chain is still processing a request.
   */
  loading: boolean,

  /**
   * Should debounce user input, true by default
   */
  debounce: boolean,

  /**
   * Change detector ref should be used together with OnPush components
   */
  ref: ChangeDetectorRef,

  /**
   * Component-level subject used to unsubscribe all active subscriptions
   */
  destroy$: Subject<void>,
}
